import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { Calendar, Save, Printer } from "lucide-react";

const fmt = (n, d = 0) => (n ?? 0).toLocaleString("en-ZA", { minimumFractionDigits: d, maximumFractionDigits: d });
const monthLabel = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  return new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleString("en-ZA", { month: "long", year: "numeric" });
};
const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const monthOptions = () => {
  const out = [];
  const now = new Date();
  for (let i = 6; i >= -1; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
};

export default function WeeklyRoastReport() {
  const { user } = useAuth();
  const canWrite = ["Owner", "ProductionManager", "Roaster"].includes(user?.role) ||
                   user?.modules?.includes("roasts");
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState({});
  const [savedTick, setSavedTick] = useState({});
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get("/weekly-roast-reports", { params: { month } });
      setData(data);
      setRows(data.weeks);
    } catch (e) {
      if (e.response?.status === 404 || e.response?.status === 502 || e.response?.status === 520) {
        setError("This page needs a fresh deploy of the backend. Please redeploy the app and refresh.");
      } else {
        setError(formatApiError(e.response?.data?.detail) || "Could not load weekly reports.");
      }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const updateField = (week_start, field, value) => {
    setRows((rs) => rs.map((r) =>
      r.week_start === week_start ? { ...r, [field]: value,
        total_roasted_soh_kg:
          (field === "bugolobi_packed_kg" ? value : r.bugolobi_packed_kg) +
          (field === "roasted_coffee_soh_kg" ? value : r.roasted_coffee_soh_kg),
      } : r
    ));
  };

  const saveRow = async (row) => {
    setSaving((s) => ({ ...s, [row.week_start]: true }));
    setError("");
    try {
      await api.post("/weekly-roast-reports", {
        week_start: row.week_start,
        month_label: month,
        bugolobi_packed_kg: Number(row.bugolobi_packed_kg || 0),
        roasted_coffee_soh_kg: Number(row.roasted_coffee_soh_kg || 0),
        comment: row.comment || "",
      });
      setSavedTick((s) => ({ ...s, [row.week_start]: true }));
      setTimeout(() => setSavedTick((s) => ({ ...s, [row.week_start]: false })), 1500);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving((s) => ({ ...s, [row.week_start]: false }));
    }
  };

  return (
    <div data-testid="weekly-roast-report-page">
      <PageHeader
        eyebrow="Operations"
        title="Weekly Roast Report"
        description={`One row per week, recapping production against the monthly sales target. Bugolobi target = ${data?.bugolobi_ratio_pct || 55}% of the apportioned weekly share.`}
        actions={(
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center gap-2 px-3 py-2 bg-card border border-border rounded text-sm">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <select value={month} onChange={(e) => setMonth(e.target.value)}
                      data-testid="weekly-month-select"
                      className="bg-transparent focus:outline-none font-mono text-sm">
                {monthOptions().map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
              </select>
            </div>
            <button onClick={() => window.print()} data-testid="weekly-print"
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-border rounded text-sm font-semibold hover:bg-secondary">
              <Printer className="w-4 h-4" /> Print
            </button>
          </div>
        )}
      />

      {error && <p className="mb-3 text-destructive text-sm" data-testid="weekly-error">{error}</p>}

      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5" data-testid="weekly-month-summary">
          <Stat label="Monthly target" value={`${fmt(data.monthly_target_kg)} kg`} accent />
          <Stat label="Roasted in month" value={`${fmt(data.roasted_in_month_kg)} kg`} />
          <Stat
            label="% to target"
            value={`${data.monthly_target_kg > 0 ? fmt(data.roasted_in_month_kg / data.monthly_target_kg * 100, 1) : "—"}%`}
            accent={data.roasted_in_month_kg >= data.monthly_target_kg}
          />
        </div>
      )}

      <div className="space-y-4">
        {rows.map((row) => (
          <WeekCard
            key={row.week_start}
            row={row}
            canWrite={canWrite}
            saving={!!saving[row.week_start]}
            saved={!!savedTick[row.week_start]}
            onChange={(f, v) => updateField(row.week_start, f, v)}
            onSave={() => saveRow(row)}
          />
        ))}
        {data && rows.length === 0 && (
          <p className="text-muted-foreground text-sm">No weeks for this month — pick another.</p>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-card border border-border rounded p-4">
      <p className="label-tiny mb-1">{label}</p>
      <p className={`text-2xl font-display font-bold tabular-nums ${accent ? "text-primary" : ""}`}>{value}</p>
    </div>
  );
}

function WeekCard({ row, canWrite, saving, saved, onChange, onSave }) {
  const weekRange = `${formatDate(row.week_start)} – ${formatDate(row.week_end)}`;
  const bugolobiVariance = row.bugolobi_target_kg > 0
    ? (row.bugolobi_packed_kg - row.bugolobi_target_kg) / row.bugolobi_target_kg * 100
    : 0;
  return (
    <div className="bg-card border border-border rounded overflow-hidden" data-testid={`week-card-${row.week_start}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-5 py-3 bg-secondary/40 border-b border-border">
        <div className="min-w-0">
          <p className="label-tiny">Week of</p>
          <p className="font-display font-bold text-base sm:text-lg">{weekRange}</p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <Pill label="In week"     value={`${fmt(row.roasted_in_week_kg, 1)} kg`} />
          <Pill label="In month"    value={`${fmt(row.roasted_in_month_kg, 1)} kg`} />
          <Pill
            label="% to target"
            value={`${row.monthly_target_kg > 0 ? fmt(row.pct_to_target, 1) : "—"}%`}
            tone={row.pct_to_target >= 100 ? "good" : row.pct_to_target >= 75 ? "warn" : "muted"}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-4 sm:p-5">
        <Field
          label={`1 kg Bugolobi packed`}
          hint={`Target: ${fmt(row.bugolobi_target_kg, 0)} kg`}
          value={row.bugolobi_packed_kg}
          onChange={(v) => onChange("bugolobi_packed_kg", v)}
          disabled={!canWrite}
          testid={`bugolobi-${row.week_start}`}
          accent={row.bugolobi_target_kg > 0 ? (bugolobiVariance >= 0 ? "good" : "muted") : null}
        />
        <Field
          label="Roasted coffee SOH"
          hint="Manual stock count"
          value={row.roasted_coffee_soh_kg}
          onChange={(v) => onChange("roasted_coffee_soh_kg", v)}
          disabled={!canWrite}
          testid={`soh-${row.week_start}`}
        />
        <ReadField label="Total roasted SOH" value={`${fmt(row.total_roasted_soh_kg, 1)} kg`}
                   hint="Bugolobi + Roasted coffee" accent />
        <ReadField
          label="Bugolobi variance"
          value={row.bugolobi_target_kg > 0
            ? `${bugolobiVariance > 0 ? "+" : ""}${bugolobiVariance.toFixed(0)}%`
            : "—"}
          hint="Actual vs target"
        />
      </div>

      <div className="px-4 sm:px-5 pb-4 sm:pb-5">
        <label className="label-tiny mb-1.5 block">Roaster comment</label>
        <textarea
          rows={2}
          value={row.comment}
          disabled={!canWrite}
          placeholder="Notes / observations / issues for this week…"
          onChange={(e) => onChange("comment", e.target.value)}
          data-testid={`comment-${row.week_start}`}
          className="w-full px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary disabled:bg-secondary/40"
        />
      </div>

      {canWrite && (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 flex justify-end">
          <button onClick={onSave} disabled={saving} data-testid={`save-${row.week_start}`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)] disabled:opacity-50">
            <Save className="w-4 h-4" />
            {saving ? "Saving…" : saved ? "Saved ✓" : "Save week"}
          </button>
        </div>
      )}
    </div>
  );
}

function Pill({ label, value, tone }) {
  const toneClass = tone === "good" ? "text-[hsl(132,18%,36%)]"
                  : tone === "warn" ? "text-[hsl(31,65%,53%)]"
                  : "text-muted-foreground";
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="label-tiny">{label}</span>
      <span className={`font-mono font-semibold ${toneClass}`}>{value}</span>
    </div>
  );
}

function Field({ label, hint, value, onChange, disabled, testid, accent }) {
  const accentClass = accent === "good" ? "border-[hsl(132,18%,46%)]"
                    : accent === "muted" ? "border-border"
                    : "border-border";
  return (
    <div>
      <label className="label-tiny mb-1.5 block">{label}</label>
      <input
        type="number" min="0" step="0.1"
        value={value || ""}
        onChange={(e) => onChange(parseFloat(e.target.value || 0))}
        disabled={disabled}
        data-testid={testid}
        className={`w-full px-3 py-2 bg-white border rounded text-sm font-mono text-right focus:outline-none focus:border-primary disabled:bg-secondary/40 ${accentClass}`}
      />
      {hint && <p className="text-[10px] text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

function ReadField({ label, value, hint, accent }) {
  return (
    <div>
      <label className="label-tiny mb-1.5 block">{label}</label>
      <div className={`px-3 py-2 rounded text-sm font-mono text-right border border-border ${accent ? "bg-primary/5 text-primary font-bold" : "bg-secondary/30"}`}>
        {value}
      </div>
      {hint && <p className="text-[10px] text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d} ${new Date(parseInt(y), parseInt(m) - 1, parseInt(d)).toLocaleString("en-ZA", { month: "short" })}`;
}
